import moment from "moment";
import type { RowData } from "@tanstack/react-table";
import type { AppFilterFn } from "./tableFeatures";

export function compareFilterValue(
  rowValue: unknown,
  filterValue: unknown,
  operator: string,
  format: string = "text",
  options: {
    min?: string | null;
    max?: string | null;
    onValue?: string;
    offValue?: string;
  } = {},
): boolean {
  // Toggle comparison options
  if (format === "toggle") {
    switch (operator) {
      case "on":
        return rowValue === options.onValue;
      case "off":
        return rowValue === options.offValue;
      case "all":
      default:
        return true;
    }
  }

  // Select/multi-select handling - "all" passes all rows
  if (format === "single-select" || format === "select" || format === "multi-select") {
    // Check for "all" value
    if (Array.isArray(filterValue) && filterValue.includes("all")) {
      return true;
    }
    if (filterValue === "all") {
      return true;
    }

    // For multi-select with anyof/allof operators
    if (Array.isArray(filterValue)) {
      const rowValueArray = Array.isArray(rowValue) ? rowValue : [rowValue];

      if (operator === "allof") {
        return filterValue.every((val) => rowValueArray.includes(val));
      }
      // anyof is default
      return filterValue.some((val) => rowValueArray.includes(val));
    }

    // Single value comparison
    return rowValue === filterValue;
  }

  // Numeric comparison options
  const { min, max } = options;
  const minParsed = min ? parseFloat(min) : -Infinity;
  const maxParsed = max ? parseFloat(max) : Infinity;

  if (["currency", "percent", "number"].includes(format)) {
    const rowValueRaw = parseFloat(rowValue as string);
    // Treat null/undefined/NaN as 0 for filtering purposes
    const rowValueParsed = Number.isNaN(rowValueRaw) ? 0 : rowValueRaw;
    const filterValueParsed = parseFloat(filterValue as string);

    switch (operator) {
      case "=":
        return rowValueParsed === filterValueParsed;
      case ">":
        return rowValueParsed > filterValueParsed;
      case "<":
        return rowValueParsed < filterValueParsed;
      case "≥":
        return rowValueParsed >= filterValueParsed;
      case "≤":
        return rowValueParsed <= filterValueParsed;
      // Below handles range thresholds
      case "-":
        return rowValueParsed >= minParsed && rowValueParsed <= maxParsed;
      default:
        return true;
    }
  }

  if (format === "date") {
    const rowValueParsed = moment(rowValue as string);
    const filterValueParsed = moment(filterValue as string);
    switch (operator) {
      case "-":
        if (!min || !max) {
          return true;
        }
        return rowValueParsed.isSameOrAfter(min, "d") && rowValueParsed.isSameOrBefore(max, "d");
      case "=":
        return rowValueParsed.isSame(filterValueParsed, "d");
      case "<":
        return rowValueParsed.isBefore(filterValueParsed, "d");
      case ">=":
        return rowValueParsed.isSameOrAfter(filterValueParsed, "d");
      case ">":
        return rowValueParsed.isAfter(filterValueParsed, "d");
      case "<=":
        return rowValueParsed.isSameOrBefore(filterValueParsed, "d");
      default:
        return true;
    }
  }

  // Case-insensitive text comparison options (default/fallback)
  const rowValueParsed =
    typeof rowValue === "object" && rowValue ?
      Object.values(rowValue)
        .map((v) => v)
        .join("")
        .toLowerCase()
    : ((rowValue as string | undefined)?.toLowerCase() ?? "");
  const filterValueParsed = (filterValue as string | undefined)?.toLowerCase() ?? "";

  switch (operator) {
    case "=":
      return rowValueParsed === filterValueParsed;
    case "≠":
      return rowValueParsed !== filterValueParsed;
    case "^":
      return rowValueParsed.startsWith(filterValueParsed);
    case "$":
      return rowValueParsed.endsWith(filterValueParsed);
    case "∌":
      return !rowValueParsed.includes(filterValueParsed);
    case "⊃":
    default:
      return rowValueParsed.includes(filterValueParsed);
  }
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const customFilterFn: AppFilterFn<any> = (row, columnId, filterValue): boolean => {
  const { op, value } = filterValue ?? {};

  if (!op) {
    return true;
  }

  const column = row.getAllCells().find((cell) => cell.column.id === columnId)?.column;
  const format = column?.columnDef.meta?.columnFilterOptions?.format ?? "text";
  const rowValue = row.getValue(columnId);

  // Handle range operator - extract min/max from value array
  if (op === "-" && Array.isArray(value)) {
    return compareFilterValue(rowValue, value, op, format, {
      min: value[0] as string,
      max: value[1] as string,
    });
  }

  return compareFilterValue(rowValue, value, op, format);
};

const withoutWhitespace = (text: string) => text.replaceAll(/\s/g, "").toLowerCase();

/**
 * The search a `SearchInput isExpandable` box offers: a comma-separated query matches when any of its
 * terms, whitespace ignored, appears in one of `fields`; otherwise it is a plain substring match. Each
 * field is searched on its own, so a term never matches across two of them.
 */
export function matchesCommaSearch(query: string, fields: ReadonlyArray<string>) {
  if (!query.includes(",")) {
    return fields.some((field) => field.toLowerCase().includes(query.toLowerCase()));
  }
  const searchable = fields.map(withoutWhitespace);
  return query
    .split(",")
    .some((term) => term.trim().length > 0 && searchable.some((field) => field.includes(withoutWhitespace(term))));
}

/**
 * Global filter running `matchesCommaSearch` over `getSearchText(row)`, which decides what is searched,
 * so it can include values no column shows. Return an array to search each field separately.
 */
export function commaSearchFilterFn<TData extends RowData>(
  getSearchText: (row: TData) => string | ReadonlyArray<string>,
): AppFilterFn<TData> {
  return (row, _columnId, filterValue) =>
    matchesCommaSearch(String(filterValue ?? ""), [getSearchText(row.original)].flat());
}
